import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { ExistingGuest } from 'voyager';

type GuestFormValues = {
  title: string;
  firstName: string;
  lastName: string;
  gender: string;
  selectedGuestId?: string;
  id?: number | string;
  externalSpaId?: number | string | null;
  loggedInUser?: { id: number };
};

type HandleExistingGuestSelectOptions = {
  form: UseFormReturn<any>;
  existingGuests: ExistingGuest[];
  titleLabelToValue?: Record<string, string>;
  defaultGender?: string;
  setValueOptions?: { shouldValidate?: boolean; shouldDirty?: boolean };
};

const emptyGuest: GuestFormValues = { title: '', firstName: '', lastName: '', gender: '' };

export const useExistingGuestSelect = ({
  form,
  existingGuests,
  titleLabelToValue,
  defaultGender = '',
  setValueOptions,
}: HandleExistingGuestSelectOptions) =>
  useCallback(
    (guestIndex: number, guestId: string | undefined) => {
      const currentGuests: GuestFormValues[] = form.getValues('guests') || [];
      const updatedGuests = [...currentGuests];

      if (!updatedGuests[guestIndex]) {
        updatedGuests[guestIndex] = { ...emptyGuest };
      }

      if (guestId) {
        const selected = existingGuests.find((g) => g.id === guestId);
        if (selected) {
          const title = titleLabelToValue
            ? titleLabelToValue[selected.title] || selected.title
            : selected.title;

          updatedGuests[guestIndex] = {
            ...updatedGuests[guestIndex],
            title,
            firstName: selected.firstName,
            lastName: selected.lastName,
            gender: selected.gender,
            selectedGuestId: guestId,
            externalSpaId: selected.externalSpaId,
            ...(!selected.loggedInUser && selected.guestId != null && { id: selected.guestId }),
            loggedInUser: selected.loggedInUser,
          };
        }
      } else {
        updatedGuests[guestIndex] = {
          ...updatedGuests[guestIndex],
          title: '',
          firstName: '',
          lastName: '',
          gender: defaultGender,
          selectedGuestId: undefined,
          id: undefined,
          externalSpaId: undefined,
          loggedInUser: undefined,
        };
      }

      form.setValue('guests', updatedGuests, setValueOptions);
    },
    [form, existingGuests, titleLabelToValue, defaultGender, setValueOptions]
  );
